import { Component } from '@verndale/core';
import Player from '@vimeo/player';
import YouTubePlayer from 'youtube-player';

class DefaultVideo extends Component {
  constructor(el) {
    super(el);

    this.isVideoPlaying = false;
    this.listenersAdded = false;
  }

  setupDefaults() {
    this.videoId = this.el.dataset.videoId;
    this.videoProvider = `${this.el.dataset.videoProvider}`.toLowerCase();

    this.dom = {
      iframeContainer: this.el.querySelector(
        '.default-video__iframe-container'
      ),
      videoProgressBar: this.el.querySelector('.default-video__progress-bar'),
      videoStatusToggle: this.el.querySelector(
        '.default-video__playback-control'
      ),
      videoStatusLabel: this.el.querySelector(
        '.default-video__playback-status-label'
      ),
      videoPauseIcon: this.el.querySelector(
        '.default-video__playback-status-icon .status-icon__pause'
      ),
      videoPlayIcon: this.el.querySelector(
        '.default-video__playback-status-icon .status-icon__play'
      ),
      videoVolumeButton: this.el.querySelector(
        '.default-video__volume-button button'
      ),
      videoVolumeControl: this.el.querySelector(
        '.default-video__volume-control'
      ),
      videoVolumeInput: this.el.querySelector(
        '.default-video__volume-control input'
      )
    };

    this.setupVideo();
  }

  addListeners() {
    if (this.listenersAdded) return;
    this.listenersAdded = true;

    this.dom.videoStatusToggle?.addEventListener(
      'click',
      this.onVideoStatusClick
    );
    this.dom.videoVolumeButton?.addEventListener(
      'click',
      this.onVideoVolumeToggle
    );
    this.dom.videoVolumeControl?.addEventListener(
      'input',
      this.onVideoVolumeChange
    );
  }

  onVideoStatusClick = e => {
    const updateVideoStatusLabel = dataAttr => {
      this.dom.videoStatusLabel.innerText = this.dom.videoStatusToggle.dataset[
        dataAttr
      ];
    };

    const updateActiveIcon = status => {
      if (status === 'play') {
        this.dom.videoPlayIcon.classList.add('active');
        this.dom.videoPauseIcon.classList.remove('active');
      } else if (status === 'pause') {
        this.dom.videoPlayIcon.classList.remove('active');
        this.dom.videoPauseIcon.classList.add('active');
      }
    };

    if (this.isVideoPlaying) {
      this.pauseVideo();
      this.isVideoPlaying = false;
      updateVideoStatusLabel('playLabel');
      updateActiveIcon('play');
    } else {
      this.playVideo();
      this.isVideoPlaying = true;
      updateVideoStatusLabel('pauseLabel');
      updateActiveIcon('pause');
    }
  };

  onVideoVolumeToggle = e => {
    const { currentTarget } = e;
    const target = currentTarget.parentNode;

    if (target.classList.contains('default-video__volume-button--open')) {
      target.classList.remove('default-video__volume-button--open');
      setTimeout(() => {
        target.classList.remove('default-video__volume-button--animate');
      }, 300);
    } else {
      target.classList.add('default-video__volume-button--open');
      target.classList.add('default-video__volume-button--animate');
    }
  };

  onVideoVolumeChange = e => {
    const { value } = e.target;
    this.setVolume(value);
  };

  cleanIframeAttributes() {
    const iframe = this.dom.iframeContainer.querySelector('iframe');
    iframe?.removeAttribute('width');
    iframe?.removeAttribute('height');
    iframe?.removeAttribute('frameborder');
  }

  setupVideo() {
    if (this.videoProvider === 'vimeo') return this.setupVimeoPlayer();
    if (this.videoProvider === 'youtube') return this.setupYouTubePlayer();
  }

  async setupVimeoPlayer() {
    if (!this.videoId) return;

    try {
      this.vimeoPlayer = new Player(this.dom.iframeContainer, {
        id: this.videoId,
        controls: false,
        loop: true,
        title: 0,
        volume: 1
      });

      await this.vimeoPlayer.ready();

      this.addListeners();

      this.vimeoPlayer.on('play', () => this.cleanIframeAttributes());

      const duration = await this.vimeoPlayer.getDuration();

      this.progressInterval = setInterval(async () => {
        const currentTime = await this.vimeoPlayer.getCurrentTime();
        this.dom.videoProgressBar.style.width = `${
          (currentTime * 100) / duration
        }%`;
      }, 300);

      this.setVolume(this.dom.videoVolumeInput.value);
    } catch (error) {
      console.error('Vimeo Player error:', error);
      this.handleVideoError();
    }
  }

  async setupYouTubePlayer() {
    this.dom.iframeContainer.style.backgroundImage = `url(https://img.youtube.com/vi/${this.videoId}/maxresdefault.jpg)`;

    const iframeElement = document.createElement('div');
    this.dom.iframeContainer.append(iframeElement);

    this.youTubePlayer = YouTubePlayer(iframeElement, {
      videoId: this.videoId,
      playerVars: {
        autohide: 1,
        showinfo: 0,
        controls: 0,
        modestbranding: 1,
        disablekb: 1,
        loop: 1,
        playlist: this.videoId,
        fs: 0
      }
    });

    this.youTubePlayer.on('ready', async () => {
      this.cleanIframeAttributes();
      this.addListeners();

      const duration = await this.youTubePlayer.getDuration();

      this.progressInterval = setInterval(async () => {
        const currentTime = await this.youTubePlayer.getCurrentTime();
        this.dom.videoProgressBar.style.width = `${
          (currentTime * 100) / duration
        }%`;
      }, 300);

      this.setVolume(this.dom.videoVolumeInput.value);
    });
  }

  handleVideoError() {
    const volumeControls = this.el.querySelector('.default-video__volume');
    if (this.dom.videoStatusToggle)
      this.dom.videoStatusToggle.style.display = 'none';
    if (volumeControls) volumeControls.style.display = 'none';
  }

  pauseVideo() {
    if (this.videoProvider === 'vimeo' && this.vimeoPlayer)
      return this.vimeoPlayer.pause();
    if (this.videoProvider === 'youtube' && this.youTubePlayer)
      return this.youTubePlayer.pauseVideo();
  }

  playVideo() {
    if (this.videoProvider === 'vimeo' && this.vimeoPlayer)
      return this.vimeoPlayer.play();
    if (this.videoProvider === 'youtube' && this.youTubePlayer) {
      this.dom.iframeContainer.classList.add(
        'default-video__iframe-container--ready'
      );
      return this.youTubePlayer.playVideo();
    }
  }

  setVolume(volume) {
    if (!this.dom.videoVolumeInput) return;

    this.dom.videoVolumeInput.style.background = `linear-gradient(to right, #f7f9fb 0%, #f7f9fb ${volume}%, #707278 ${volume}%, #707278 100%)`;

    if (this.videoProvider === 'vimeo' && this.vimeoPlayer) {
      this.vimeoPlayer
        .setVolume(volume / 100)
        .catch(err => console.error('Vimeo setVolume error:', err));
    }
    if (this.videoProvider === 'youtube' && this.youTubePlayer) {
      this.youTubePlayer
        .setVolume(volume)
        .catch(err => console.error('YouTube setVolume error:', err));
    }
  }
}

export default DefaultVideo;
